import { create } from "../../common/utils/zustand_state_manager";
import { PaginationProperties, SetPaginationProperties, InitializePaginationProperties, InitializeSetPaginationProperties } from "../utils/pagination_property";
import { useCallback, useEffect, useState } from "react";
import { LogTipoTabela } from "../utils/log_tabela_tipo";
import { IGetAllLogAlteracaoByParentUseCase } from "../presentation/useCases/read/get_all_log_alteracao_by_parent_use_case";
import { IGetAllLogAlteracaoUseCase } from "../presentation/useCases/read/get_all_log_alteracao_use_case";
import { LogAlteracao } from "../domain/models/log_alteracao";
import { processError } from "../utils/base_error";

/**
 * ChangeHistoryState
 */

export type ChangeHistoryState = {
  loading: boolean;
  openChangeHistoryModal: boolean;
  showDateColumn: boolean;
  alteracoes: LogAlteracao[];
  pagination: PaginationProperties & SetPaginationProperties;
  handleOpenChangeHistory: (idTabela: number, tipoTabela: LogTipoTabela, isFromParent: boolean) => Promise<void>;
  handleCloseChangeHistory: () => Promise<void>;
  handleChangeHistoryPageChange: (page: number) => Promise<void>;
  updateShowDateColumn: (newValue: boolean) => void;
};

/**
 * useChangeHistory
 */

const useChangeHistoryPagination = create<PaginationProperties & SetPaginationProperties>((set) => ({
  ...InitializePaginationProperties({ rowsPerPage: 100 }),
  ...InitializeSetPaginationProperties(set),
}));

/**
 * useChangeHistory
 */

type Props = {
  getAllLogAlteracaoUseCase: IGetAllLogAlteracaoUseCase;
  getAllLogAlteracaoByParentUseCase: IGetAllLogAlteracaoByParentUseCase;
  onSetError: (value: string) => void;
};

export const useChangeHistory = ({
  getAllLogAlteracaoUseCase,
  getAllLogAlteracaoByParentUseCase,
  onSetError,
}: Props): ChangeHistoryState => {
  const pagination = useChangeHistoryPagination();

  const [loading, setLoading] = useState(false);

  const [alteracoes, setAlteracoes] = useState<LogAlteracao[]>();

  const [isParent, setIsParent] = useState<boolean>(false);
  const [showDateColumn, setShowDateColumn] = useState(true);

  const [selectedIdTabela, setSelectedIdTabela] = useState<number>();
  const [selectedTipoTabela, setSelectedTipoTabela] = useState<LogTipoTabela>();

  const [openChangeHistoryModal, setOpenChangeHistoryModal] = useState(false);

  /**
   * Funcoes de carregamento
   */

  const loadLogAlteracao = useCallback(async ({
    tipoTabela,
    idTabela,
    page,
    rowsPerPage,
  }: {
    tipoTabela: LogTipoTabela,
    idTabela: number,
    page?: number,
    rowsPerPage?: number,
  }) => {
    try {
      const filteredPage = page ?? pagination.page;
      const filteredRowsPerPage = rowsPerPage ?? pagination.rowsPerPage;

      await getAllLogAlteracaoUseCase.getAllLogAlteracao(
        tipoTabela,
        idTabela,
        filteredPage,
        filteredRowsPerPage,
      );

      setAlteracoes(getAllLogAlteracaoUseCase.items);

      setSelectedIdTabela(idTabela);
      setSelectedTipoTabela(tipoTabela);

      setIsParent(false);

      pagination.setTotalItems(getAllLogAlteracaoUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar lista de alterações!');
    }
  }, [
    pagination,
    getAllLogAlteracaoUseCase,
  ]);

  const loadLogAlteracaoByParent = useCallback(async ({
    tipoTabela,
    idTabela,
    page,
    rowsPerPage,
  }: {
    tipoTabela: LogTipoTabela,
    idTabela: number,
    page?: number,
    rowsPerPage?: number,
  }) => {
    try {
      const filteredPage = page ?? pagination.page;
      const filteredRowsPerPage = rowsPerPage ?? pagination.rowsPerPage;

      await getAllLogAlteracaoByParentUseCase.getAllLogAlteracaoByParent(
        tipoTabela,
        idTabela,
        filteredPage,
        filteredRowsPerPage,
      );

      setAlteracoes(getAllLogAlteracaoByParentUseCase.items);

      setSelectedIdTabela(idTabela);
      setSelectedTipoTabela(tipoTabela);

      setIsParent(true);

      pagination.setTotalItems(getAllLogAlteracaoByParentUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar lista de alterações!');
    }
  }, [
    pagination,
    getAllLogAlteracaoByParentUseCase,
  ]);

  /**
   * useEffect
   */

  useEffect(() => {
    useChangeHistoryPagination.getState().setPage(1);

    setSelectedIdTabela(undefined);
    setSelectedTipoTabela(undefined);

    setIsParent(false);
  }, []);

  /**
   * Eventos
   */

  const handleOpenChangeHistory = useCallback(async (
    idTabela: number,
    tipoTabela: LogTipoTabela,
    isFromParent: boolean,
  ) => {
    try {
      setLoading(true);

      setAlteracoes(undefined);
      setOpenChangeHistoryModal(true);

      setSelectedIdTabela(idTabela);
      setSelectedTipoTabela(tipoTabela);

      setIsParent(isFromParent);

      pagination.setPage(1);

      if (isFromParent) {
        await loadLogAlteracaoByParent({ idTabela, tipoTabela, page: 1 });
      } else {
        await loadLogAlteracao({ tipoTabela, idTabela, page: 1 });
      }
    } catch (e: unknown) {
      onSetError(processError(e, 'Falha ao buscar lista de alterações!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadLogAlteracaoByParent,
    loadLogAlteracao,
    onSetError,
  ]);

  const handleCloseChangeHistory = useCallback(async () => {
    setOpenChangeHistoryModal(false);
  }, []);

  const handleChangeHistoryPageChange = useCallback(async (
    page: number,
  ) => {
    try {
      setLoading(true);

      pagination.setPage(page + 1);

      if (selectedIdTabela && selectedTipoTabela) {
        if (isParent === true) {
          await loadLogAlteracaoByParent({ idTabela: selectedIdTabela, tipoTabela: selectedTipoTabela, page: page + 1 });
        } else {
          await loadLogAlteracao({ idTabela: selectedIdTabela, tipoTabela: selectedTipoTabela, page: page + 1 });
        }
      }
    } catch (e: unknown) {
      onSetError(processError(e, 'Falha ao buscar lista de alterações!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    selectedIdTabela,
    selectedTipoTabela,
    isParent,
    loadLogAlteracaoByParent,
    loadLogAlteracao,
    onSetError,
  ]);

  const updateShowDateColumn = useCallback((newValue: boolean) => {
    setShowDateColumn(newValue);
  }, [
  ]);

  return {
    loading,
    openChangeHistoryModal,
    alteracoes: alteracoes ?? [],
    showDateColumn,
    pagination,
    handleOpenChangeHistory,
    handleCloseChangeHistory,
    handleChangeHistoryPageChange,
    updateShowDateColumn,
  }
}